import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Menu, 
  X, 
  Home, 
  Ticket, 
  MessageSquare, 
  Users, 
  Shield, 
  Settings, 
  Bell, 
  LogOut, 
  ChevronDown, 
  FolderTree, 
  UserCog,
  ChevronLeft,
  ChevronRight,
  Wifi,
  WifiOff
} from 'lucide-react';
import toast from 'react-hot-toast';
import notificationService from '../services/notificationService';
import { NotificationEvents } from '../utils/notificationEvents';

const PrivateLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Enhanced notification state
  const [unreadCount, setUnreadCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastNotificationCheck, setLastNotificationCheck] = useState(null);
  const [showConnectionStatus, setShowConnectionStatus] = useState(false);
  
  // Refs for cleanup
  const echoChannelRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    user, 
    logout, 
    getUserDisplayName, 
    isAdmin, 
    isAgent, 
    isCustomerCare, 
    hasPermission,
    hasAnyPermission,
    canManageUsers,
    canManageSystem,
    getDebugInfo
  } = useAuth();

  // Debug logging - remove in production
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('PrivateLayout - Auth Debug:', getDebugInfo?.());
    }
  }, [getDebugInfo]);

  // Fetch unread count from API
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationService.getUnreadCount();
      const count = response?.data?.unread_count || 0;
      setUnreadCount(count);
      setLastNotificationCheck(new Date());
      return count;
    } catch (error) {
      console.warn('Failed to fetch unread count:', error);
      // Don't show error to user for background polling
      return null;
    }
  }, []);

  // Setup WebSocket connection using Laravel Echo
  const setupWebSocketConnection = useCallback(() => {
    if (!user?.id || !window.Echo) {
      console.warn('WebSocket setup failed: Missing user ID or Echo instance');
      return;
    }

    try {
      // Clean up existing connection
      if (echoChannelRef.current) {
        window.Echo.leave(`App.Models.User.${user.id}`);
        echoChannelRef.current = null;
      }

      // Setup private channel for user notifications
      const channel = window.Echo.private(`App.Models.User.${user.id}`);
      
      echoChannelRef.current = channel;

      // Listen for notifications
      channel.notification((notification) => {
        console.log('Real-time notification received:', notification);
        
        // Process the notification
        const processedNotification = notificationService.processWebSocketNotification(notification);
        
        // Update unread count if notification is unread
        if (!notification.read_at) {
          setUnreadCount(prev => prev + 1);
        }

        // Show toast notification
        const priority = notificationService.getNotificationPriority(notification);
        const message = notificationService.formatNotificationMessage(notification);
        
        // Different toast types based on notification type
        if (notification.type?.includes('Escalated')) {
          toast.error(message, { duration: 6000 });
        } else if (notification.type?.includes('Assigned')) {
          toast.success(message, { duration: 4000 });
        } else if (notification.type?.includes('Forwarded')) {
          toast(message, { duration: 4000, icon: '📤' });
        } else {
          toast.info(message, { duration: 3000 });
        }

        // Dispatch custom event for other components
        NotificationEvents.handleRealTimeNotification(processedNotification);
      });

      // Connection status events
      channel.subscribed(() => {
        console.log('WebSocket connected successfully');
        setConnectionStatus('connected');
        setShowConnectionStatus(false);
        NotificationEvents.connectionEstablished();
      });

      channel.error((error) => {
        console.error('WebSocket connection error:', error);
        setConnectionStatus('error');
        setShowConnectionStatus(true);
        NotificationEvents.error(error, 'websocket_connection');
        
        // Auto-hide error status after 10 seconds
        setTimeout(() => setShowConnectionStatus(false), 10000);
      });

      // Listen for disconnection
      window.Echo.connector.pusher.connection.bind('disconnected', () => {
        console.log('WebSocket disconnected');
        setConnectionStatus('disconnected');
        setShowConnectionStatus(true);
        NotificationEvents.connectionLost();
        
        // Auto-hide status after 5 seconds
        setTimeout(() => setShowConnectionStatus(false), 5000);
      });

      // Listen for reconnection
      window.Echo.connector.pusher.connection.bind('connecting', () => {
        console.log('WebSocket reconnecting...');
        setConnectionStatus('reconnecting');
        setShowConnectionStatus(true);
        NotificationEvents.connectionReconnecting();
      });

    } catch (error) {
      console.error('Failed to setup WebSocket connection:', error);
      setConnectionStatus('error');
      NotificationEvents.error(error, 'websocket_setup');
    }
  }, [user?.id]);

  // Setup fallback polling for when WebSocket is not available
  const setupFallbackPolling = useCallback(() => {
    // Clear existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Only setup polling if WebSocket is not connected
    if (connectionStatus !== 'connected' && user) {
      console.log('Setting up fallback polling for notifications');
      
      pollingIntervalRef.current = setInterval(async () => {
        const count = await fetchUnreadCount();
        
        // If count is significantly different, something might have happened
        if (count !== null && Math.abs(count - unreadCount) > 0) {
          console.log(`Polling detected count change: ${unreadCount} -> ${count}`);
        }
      }, 60000); // Poll every minute as fallback
    }
  }, [connectionStatus, user, fetchUnreadCount, unreadCount]);

  // Initialize notification system
  useEffect(() => {
    if (!user) return;

    // Initial count fetch
    fetchUnreadCount();

    // Try to setup WebSocket connection
    if (window.Echo) {
      setupWebSocketConnection();
    } else {
      console.warn('Laravel Echo not available, using polling fallback');
      setConnectionStatus('polling');
      setupFallbackPolling();
    }

    return () => {
      // Cleanup WebSocket connection
      if (echoChannelRef.current && user?.id) {
        try {
          window.Echo.leave(`App.Models.User.${user.id}`);
        } catch (error) {
          console.warn('Error leaving Echo channel:', error);
        }
      }
      
      // Cleanup polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      
      // Cleanup reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [user, setupWebSocketConnection, fetchUnreadCount, setupFallbackPolling]);

  // Setup fallback polling when WebSocket connection changes
  useEffect(() => {
    setupFallbackPolling();
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [connectionStatus, setupFallbackPolling]);

  // Listen for custom notification events from other components
  useEffect(() => {
    const handleNotificationUpdate = (event) => {
      const { detail } = event;
      
      console.log('Notification event received:', detail);

      switch (detail.type) {
        case 'notification_read':
          setUnreadCount(prev => Math.max(0, prev - 1));
          break;
          
        case 'new_notification':
        case 'real_time':
          if (detail.incrementCount && !detail.notification?.read_at) {
            setUnreadCount(prev => prev + 1);
          }
          break;
          
        case 'all_read':
        case 'bulk_read':
          setUnreadCount(0);
          break;
          
        case 'notification_deleted':
          if (detail.decrementCount) {
            setUnreadCount(prev => Math.max(0, prev - 1));
          }
          break;
          
        case 'bulk_delete':
          if (detail.decrementCount) {
            setUnreadCount(prev => Math.max(0, prev - detail.decrementCount));
          }
          break;
          
        case 'count_update':
          if (typeof detail.count === 'number') {
            setUnreadCount(detail.count);
          }
          break;
          
        default:
          if (detail.incrementCount) {
            setUnreadCount(prev => prev + 1);
          }
      }
    };

    const handleConnectionUpdate = (event) => {
      const { detail } = event;
      
      switch (detail.type) {
        case 'connected':
          setConnectionStatus('connected');
          setShowConnectionStatus(false);
          // Refresh count when connected
          fetchUnreadCount();
          break;
          
        case 'disconnected':
          setConnectionStatus('disconnected');
          setShowConnectionStatus(true);
          // Try to reconnect after 5 seconds
          reconnectTimeoutRef.current = setTimeout(() => {
            if (window.Echo && user?.id) {
              setupWebSocketConnection();
            }
          }, 5000);
          break;
          
        case 'reconnecting':
          setConnectionStatus('reconnecting');
          setShowConnectionStatus(true);
          break;
      }
    };

    const handleNotificationError = (event) => {
      console.error('Notification error:', event.detail);
      
      if (event.detail.context === 'websocket_connection') {
        // Switch to polling mode on WebSocket errors
        setConnectionStatus('polling');
        setupFallbackPolling();
      }
    };

    const handleNotificationRefresh = () => {
      fetchUnreadCount();
    };

    // Add event listeners
    window.addEventListener('notificationUpdate', handleNotificationUpdate);
    window.addEventListener('notificationConnection', handleConnectionUpdate);
    window.addEventListener('notificationError', handleNotificationError);
    window.addEventListener('notificationRefresh', handleNotificationRefresh);

    return () => {
      window.removeEventListener('notificationUpdate', handleNotificationUpdate);
      window.removeEventListener('notificationConnection', handleConnectionUpdate);
      window.removeEventListener('notificationError', handleNotificationError);
      window.removeEventListener('notificationRefresh', handleNotificationRefresh);
    };
  }, [fetchUnreadCount, setupWebSocketConnection, setupFallbackPolling, user?.id]);

  // Handle responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      
      if (mobile) {
        setSidebarOpen(false);
      }
      
      if (window.innerWidth >= 768 && window.innerWidth < 1024) {
        setSidebarCollapsed(true);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownOpen && !event.target.closest('.profile-dropdown')) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileDropdownOpen]);

  // Close sidebar on route change for mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const handleLogout = useCallback(async () => {
    try {
      // Clean up all notification resources
      if (echoChannelRef.current && user?.id) {
        try {
          window.Echo.leave(`App.Models.User.${user.id}`);
        } catch (error) {
          console.warn('Error leaving Echo channel during logout:', error);
        }
      }
      
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  }, [logout, navigate, user?.id]);

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setSidebarOpen(prev => !prev);
    } else {
      setSidebarCollapsed(prev => !prev);
    }
  }, [isMobile]);

  // Enhanced notification click handler
  const handleNotificationClick = useCallback((e) => {
    // Don't automatically mark notifications as read
    // Let the notifications page handle the read logic
    console.log('Notification bell clicked, navigating to notifications page');
  }, []);

  // Profile image handling
  const [imageLoadError, setImageLoadError] = useState(false);
  
  const profileImagePath = user?.profile_image;
  const profileImageUrl = profileImagePath ? 
    (profileImagePath.startsWith('http') ? 
      profileImagePath : 
      profileImagePath.startsWith('/storage/') ?
        profileImagePath :
        `/storage/${profileImagePath}`
    ) : null;
    
  const userInitial = user?.name?.charAt(0)?.toUpperCase() || 'U';

  useEffect(() => {
    setImageLoadError(false);
  }, [profileImageUrl]);

  // Enhanced navigation items
  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: Home,
      show: true
    },
    {
      name: 'Tickets',
      href: '/tickets',
      icon: Ticket,
      show: true
    },

    {
      name: 'Categories',
      href: '/categories',
      icon: FolderTree,
      show: isAdmin() || 
            isAgent() || 
            isCustomerCare() || 
            (hasAnyPermission && hasAnyPermission([
              'categories.view', 
              'categories.view-tree', 
              'categories.create',
              'categories.update'
            ]))
    },
    {
      name: 'Users',
      href: '/users',
      icon: Users,
      show: isAdmin() || 
            canManageUsers() || 
            (hasAnyPermission && hasAnyPermission([
              'users.view', 
              'users.view-all',
              'manage-users'
            ]))
    },
    {
      name: 'Roles',
      href: '/roles',
      icon: Shield,
      show: isAdmin() || 
            canManageSystem() || 
            (hasAnyPermission && hasAnyPermission([
              'roles.view',
              'roles.create',
              'roles.update',
              'manage-system'
            ]))
    },
    {
      name: 'Settings',
      href: '/settings',
      icon: Settings,
      show: isAdmin() || 
            isAgent() || 
            isCustomerCare() || 
            (hasAnyPermission && hasAnyPermission([
              'escalation.config',
              'escalation.view',
              'escalation.update',
              'config.view',
              'config.update',
              'manage-system'
            ]))
    }
  ];

  const isActive = useCallback((path) => {
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  const visibleNavItems = navigationItems.filter(item => {
    if (!hasPermission && !isAdmin) {
      return ['Dashboard', 'Tickets', 'Notifications'].includes(item.name);
    }
    return item.show;
  });

  // Get connection status display
  const getConnectionStatusDisplay = () => {
    switch (connectionStatus) {
      case 'connected':
        return { icon: Wifi, color: 'text-green-500', text: 'Real-time notifications active' };
      case 'reconnecting':
        return { icon: Wifi, color: 'text-yellow-500', text: 'Reconnecting...' };
      case 'polling':
        return { icon: Wifi, color: 'text-blue-500', text: 'Using backup sync' };
      case 'disconnected':
      case 'error':
      default:
        return { icon: WifiOff, color: 'text-red-500', text: 'Offline mode' };
    }
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      {/* Connection Status Toast */}
      {showConnectionStatus && (
        <div className="fixed top-4 right-4 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-sm">
          <div className="flex items-center space-x-2">
            {(() => {
              const statusDisplay = getConnectionStatusDisplay();
              const StatusIcon = statusDisplay.icon;
              return (
                <>
                  <StatusIcon className={`h-4 w-4 ${statusDisplay.color}`} />
                  <span className="text-sm text-gray-700">{statusDisplay.text}</span>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          ${isMobile ? 'fixed' : 'relative'} 
          inset-y-0 left-0 z-50 
          ${isMobile ? 'w-64' : sidebarCollapsed ? 'w-16' : 'w-64'}
          bg-white shadow-lg transform transition-all duration-300 ease-in-out 
          ${isMobile ? (sidebarOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'}
          flex flex-col
        `}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 flex-shrink-0">
          <Link to="/dashboard" className="flex items-center min-w-0">
            <div className="flex-shrink-0">
              <svg
                className="h-8 w-8 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            {!sidebarCollapsed && (
              <span className="ml-2 text-xl font-bold text-gray-900 truncate">
                IMS
              </span>
            )}
          </Link>
          
          <button
            className="p-1 rounded-md hover:bg-gray-100 transition-colors"
            onClick={toggleSidebar}
          >
            {isMobile ? (
              <X className="h-6 w-6 text-gray-600" />
            ) : sidebarCollapsed ? (
              <ChevronRight className="h-5 w-5 text-gray-600" />
            ) : (
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 overflow-y-auto">
          <div className="space-y-1">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const isActiveItem = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    group flex items-center px-3 py-2.5 
                    text-sm font-medium rounded-lg transition-all duration-200 relative
                    ${isActiveItem
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                    ${sidebarCollapsed ? 'justify-center' : ''}
                  `}
                  onClick={() => isMobile && setSidebarOpen(false)}
                  title={sidebarCollapsed ? item.name : undefined}
                >
                  <Icon
                    className={`
                      h-5 w-5 flex-shrink-0 transition-colors
                      ${sidebarCollapsed ? '' : 'mr-3'}
                      ${isActiveItem
                        ? 'text-blue-600'
                        : 'text-gray-400 group-hover:text-gray-500'
                      }
                    `}
                  />
                  {!sidebarCollapsed && (
                    <span className="truncate flex-1">{item.name}</span>
                  )}
                  {/* Badge for notifications */}
                  {item.badge && (
                    <span className={`
                      ${sidebarCollapsed ? 'absolute -top-1 -right-1' : 'ml-2'}
                      inline-flex items-center justify-center h-5 w-5 text-xs font-medium
                      bg-red-500 text-white rounded-full animate-pulse
                    `}>
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User info at bottom of sidebar */}
        <div className="flex-shrink-0 border-t border-gray-200 p-4">
          {!sidebarCollapsed ? (
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {profileImageUrl && profileImageUrl.trim() && !imageLoadError ? (
                  <img
                    className="h-10 w-10 rounded-full object-cover"
                    src={profileImageUrl}
                    alt={getUserDisplayName()}
                    onError={(e) => {
                      console.log('Image load error:', e.target.src);
                      setImageLoadError(true);
                    }}
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {userInitial}
                    </span>
                  </div>
                )}
              </div>
              <div className="ml-3 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-700 truncate">
                  {getUserDisplayName()}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.user_type || 'User'}
                </p>
                {user?.user_id_number && (
                  <p className="text-xs text-gray-400 mt-1">
                    ID: <span className="border border-orange-500 bg-orange-100 p-1 rounded">{user.user_id_number}</span>
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              {profileImageUrl && profileImageUrl.trim() && !imageLoadError ? (
                <img
                  className="h-8 w-8 rounded-full object-cover"
                  src={profileImageUrl}
                  alt={getUserDisplayName()}
                  title={getUserDisplayName()}
                  onError={(e) => {
                    console.log('Image load error (collapsed):', e.target.src);
                    setImageLoadError(true);
                  }}
                />
              ) : (
                <div 
                  className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center"
                  title={getUserDisplayName()}
                >
                  <span className="text-white text-xs font-medium">
                    {userInitial}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top navigation bar */}
        <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            {/* Mobile menu button */}
            {isMobile && (
              <button
                className="p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open sidebar"
              >
                <Menu className="h-6 w-6" />
              </button>
            )}

            {/* Desktop toggle button */}
            {!isMobile && (
              <button
                className="p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                onClick={toggleSidebar}
                aria-label="Toggle sidebar"
              >
                <Menu className="h-5 w-5" />
              </button>
            )}

            {/* Mobile logo */}
            {isMobile && (
              <div className="flex-1 flex justify-center">
                <Link to="/dashboard" className="flex items-center">
                  <svg
                    className="h-8 w-8 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </Link>
              </div>
            )}

            {/* Spacer for desktop */}
            {!isMobile && <div className="flex-1" />}

            {/* Right side actions */}
            <div className="flex items-center space-x-3">
              {/* Real-time Notifications with better animation */}
              <Link
                to="/notifications"
                onClick={handleNotificationClick}
                className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative">{unreadCount > 9 ? '9+' : unreadCount}</span>
                  </span>
                )}
              </Link>

              {/* Profile dropdown */}
              <div className="relative profile-dropdown">
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center p-1 text-sm rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  aria-label="User menu"
                >
                  {profileImageUrl && profileImageUrl.trim() && !imageLoadError ? (
                    <img
                      className="h-8 w-8 rounded-full object-cover"
                      src={profileImageUrl}
                      alt={getUserDisplayName()}
                      onError={(e) => {
                        console.log('Image load error (dropdown):', e.target.src);
                        setImageLoadError(true);
                      }}
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {userInitial}
                      </span>
                    </div>
                  )}
                  <ChevronDown className="hidden sm:block ml-2 h-4 w-4 text-gray-500" />
                </button>

                {/* Dropdown menu */}
                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {getUserDisplayName()}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {user?.email}
                      </p>
                      {user?.user_id_number && (
                        <p className="text-xs text-gray-800 mt-1">
                          ID: <span className="border border-orange-500 bg-orange-100 p-1 rounded">{user.user_id_number}</span>
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {user?.user_type || 'User'}
                      </p>
                      
                      {/* Connection status in dropdown */}
                      <div className="flex items-center mt-2 text-xs">
                        {(() => {
                          const statusDisplay = getConnectionStatusDisplay();
                          const StatusIcon = statusDisplay.icon;
                          return (
                            <>
                              <StatusIcon className={`h-3 w-3 mr-1 ${statusDisplay.color}`} />
                              <span className={statusDisplay.color}>{statusDisplay.text}</span>
                            </>
                          );
                        })()}
                      </div>
                      
                      {lastNotificationCheck && (
                        <p className="text-xs text-gray-400 mt-1">
                          Last sync: {new Date(lastNotificationCheck).toLocaleTimeString()}
                        </p>
                      )}
                      
                      {process.env.NODE_ENV === 'development' && (
                        <p className="text-xs text-red-600 mt-1">
                          Roles: {user?.roles?.join(', ') || 'None'}
                        </p>
                      )}
                    </div>
                    
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setProfileDropdownOpen(false)}
                    >
                      <UserCog className="h-4 w-4 mr-3 text-gray-400" />
                      Profile Settings
                    </Link>
                    
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <LogOut className="h-4 w-4 mr-3 text-gray-400" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {/* Mobile bottom navigation */}
      {isMobile && (
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30">
          <div className="grid grid-cols-4 gap-1 py-2">
            {visibleNavItems.slice(0, 4).map((item) => {
              const Icon = item.icon;
              const isActiveItem = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    flex flex-col items-center py-1 px-2 text-xs font-medium transition-colors relative
                    ${isActiveItem
                      ? 'text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                    }
                  `}
                >
                  <Icon className="h-5 w-5 mb-1" />
                  <span className="truncate">{item.name}</span>
                  {item.badge && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
};

export default PrivateLayout;